import { DatePipe } from '@angular/common'
import { Component, WritableSignal, effect, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellType, DataMapEditor, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, Grid, ListItem } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { DepartmentChartComponent } from './department-chart/department-chart.component'
import { ProjectedHoursChartComponent } from './projected-hours-chart/projected-hours-chart.component'

@Component({
    templateUrl: './projected-hours.component.html',
    styleUrls: ['./projected-hours.component.css'],
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjInputModule,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        ProjectedHoursChartComponent,
        DepartmentChartComponent,
        DatePipe,
    ],
})

// TODO: Allow the filters to show more items.
export class ProjectedHoursComponent extends BaseComponent {
    faSpinner = faSpinner
    active = 1
    grid: Grid<ProjectedHoursItem>
    scrollPosition: any

    maxQuarters = 16
    quartersToShow = signal(16)

    activeJobsList: ListItem<string>[] = []
    allItems: ProjectedHoursItem[] = []

    quartersCols: QuarterColumn[] = []
    columnBindings = [] as string[]
    monthAbbrs = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    openMonth = new Date()

    quarterlyValues: HoursProjection[] = []

    boolList: ListItem<boolean>[] = [new ListItem(true, 'Yes'), new ListItem(false, 'No')]
    showZeroHoursRemaining = signal(true)
    resizeColumns = false

    departments: WritableSignal<ListItem<string>[]> = signal([])

    constructor() {
        super()

        effect(() => {
            this.showZeroHoursRemaining()
            this.quartersToShow()
            this.resetColumns()
        })

        this.grid = new Grid<ProjectedHoursItem>(this.services, 'id', this)
            .wrapColumnHeaders(80)
            .filterBy(item => [item.jobDisplay])
            .isReadOnly(false)
            .editOnDoubleClick(false)
            .showAggregateRow(true)

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('/api/ProjectedHours/GetProjectedHoursData'),
                    this.services.data.get('/api/ProjectedHours/GetHoursProjections'),
                    this.services.data.get('/api/Projections/GetOpenMonth'),
                    this.services.data.vpJobs.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()

                        if (this.activeJobsList.length == 0) {
                            const jobsList = this.services.data.vpJobs.items.map(
                                x => new ListItem(x.id.trim(), x.id.trim() + ' - ' + x.name),
                            )
                            jobsList.forEach(x => (x.selected = true))
                            this.activeJobsList = jobsList
                        }

                        this.allItems = result[0]

                        this.departments.set(
                            this.allItems
                                .map(x => x.departmentName)
                                .distinct()
                                .orderBy(x => x)
                                .map(x => new ListItem(x.split(' ')[0], x)),
                        )

                        this.quarterlyValues = result[1]
                        this.openMonth = new Date(result[2])
                        this.resetColumns()
                    },
                }),
        )
    }

    resetColumns() {
        if (!this.grid.flexGrid) return
        this.resizeColumns = true
        this.scrollPosition = this.grid.flexGrid.scrollPosition

        this.quartersCols = []
        setTimeout(() => this.generateColumns(), 10)
    }

    generateColumns() {
        const columns = [] as QuarterColumn[]
        const columnBindings = [] as string[]
        const currentMonth = this.openMonth.getMonth() // 0 = Jan, 11 = Dec
        const currentQuarter = Math.floor(currentMonth / 3) + 1
        const currentYear = this.openMonth.getFullYear()
        for (let i = 0; i < this.maxQuarters; i++) {
            const totalQuartersAhead = currentQuarter - 1 + i
            const year = currentYear + Math.floor(totalQuartersAhead / 4)
            const quarter = (totalQuartersAhead % 4) + 1

            const startMonthIndex = (quarter - 1) * 3
            const endMonthIndex = startMonthIndex + 2

            // If it's the first quarter in the loop (i === 0), adjust the start month based on the open month
            let adjustedStartMonthIndex = startMonthIndex
            if (i === 0) {
                const currentMonthIndex = this.openMonth.getMonth() // 0 = Jan, 1 = Feb, etc.
                if (currentMonthIndex > startMonthIndex && currentMonthIndex <= endMonthIndex) {
                    adjustedStartMonthIndex = currentMonthIndex
                }
            }
            const monthRange = `${this.monthAbbrs[adjustedStartMonthIndex]} – ${this.monthAbbrs[endMonthIndex]} '${year - 2000}`

            const isNewYear = quarter === 4 && i !== 0

            columnBindings.push(`YQ${year}${quarter}`)
            if (i < this.quartersToShow()) {
                columns.push({
                    header: monthRange,
                    binding: `YQ${year}${quarter}`,
                    width: 90,
                    isReadOnly: false,
                })

                if (isNewYear || i === this.quartersToShow() - 1) {
                    columns.push({
                        header: `${year} Totals`,
                        binding: `Totals${year}`,
                        width: 100,
                        isReadOnly: true,
                    })
                }
            }
        }
        this.quartersCols = columns
        this.columnBindings = columnBindings
        this.grid.flexGrid.refresh()
        this.updateWithQuarterlyData()
    }

    updateWithQuarterlyData() {
        // When we regenerate the data, the selection changes, so restore it,
        // and the selection will automatically advance as expected
        const currentSelection = this.grid.flexGrid.selection

        this.grid.items = this.allItems.filter(x => this.activeJobsList.find(y => y.value === x.job && y.selected))
        if (!this.showZeroHoursRemaining()) {
            this.grid.items = this.grid.items.filter(x => x.remainingHours > 0.1 || x.remainingHours < -0.1)
        }
        const items = this.grid.items as ProjectedHoursItem[]
        for (const item of items) {
            // Loop through the quarterly values and set the values in the grid item.
            let total = 0
            const yearlyTotals: Record<string, number> = {}

            for (const binding of this.columnBindings) {
                const yearQuarter = parseInt(binding.substring(2))
                const year = Math.floor(yearQuarter / 10) // Extract year from binding
                const value = this.quarterlyValues.find(
                    x => x.job === item.job && x.phase === item.phase && x.yearQuarter === parseInt(binding.substring(2)),
                )
                if (value) {
                    total += value.hours
                    item[binding] = value.hours

                    // Add to yearly total
                    if (!yearlyTotals[year]) {
                        yearlyTotals[year] = 0
                    }
                    yearlyTotals[year] += value.hours
                } else {
                    // TODO: Should we show 0 or leave it blank?
                    //item[binding] = 0
                }
            }

            // Assign yearly totals to Totals${year} properties
            for (const year in yearlyTotals) {
                item[`Totals${year}`] = yearlyTotals[year]
            }
            item.totalHoursProjected = total
            item.variance = item.remainingHours - total
        }

        this.grid.flexGrid.refresh()

        if (this.resizeColumns) {
            this.grid.flexGrid.autoSizeColumns(0, 1)
            this.resizeColumns = false
        }

        this.grid.flexGrid.selection = currentSelection
        this.grid.flexGrid.scrollPosition = this.scrollPosition
    }

    mainGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const col = panel.grid.columns[c] as any
            const rowItem = row.dataItem

            // Reset cell class and style due to cell recycling
            cell.classList.remove('yellow-cell')
            cell.style.fontWeight = 'normal'
            cell.classList.remove('orange-cell')

            if (col.binding.startsWith('YQ')) cell.classList.add('yellow-cell')
            if (col.binding.startsWith('Totals')) cell.style.fontWeight = 'bold'

            if (col.binding.startsWith('variance') && (rowItem.variance < -0.1 || rowItem.variance > 0.1)) {
                cell.style.fontWeight = 'bold'
                cell.classList.add('orange-cell')
            }
        }
    }

    cellEditEnding(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return

        const row = flexGrid.rows[args.row]
        const col = flexGrid.columns[args.col] as any

        const newVal = row.dataItem[col.binding]

        console.log('cellEditEnding', newVal)
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const row = flexGrid.rows[args.row]
        const col = flexGrid.columns[args.col] as any
        const rowItem = row.dataItem

        // Get the new and old values
        let newVal = row.dataItem[col.binding]
        const oldVal = args.data

        // Don't update the grid if the value is the same
        if (newVal === oldVal) return

        // Validate the new value
        if (isNaN(Number(newVal))) {
            // If the value is not a number, reset it to the old value
            row.dataItem[col.binding] = oldVal
            this.services.notification.error('Please enter a valid number.')
            return
        }

        // Convert the value to a number
        newVal = Number(newVal)
        rowItem[col.binding] = newVal === 0 ? null : newVal

        // Update the projection with the new value
        this.updateProjection([rowItem], col.binding)
    }

    updateProjection(items: ProjectedHoursItem[], binding: string) {
        this.scrollPosition = this.grid.flexGrid.scrollPosition
        try {
            const dtos = [] as HoursProjection[]
            for (const item of items) {
                dtos.push({
                    job: item.job,
                    phase: item.phase,
                    yearQuarter: parseInt(binding.substring(2, binding.length)),
                    hours: item[binding],
                })

                // Update the grid item with the new value.
                const value = this.quarterlyValues.find(
                    x => x.job === item.job && x.phase === item.phase && x.yearQuarter === parseInt(binding.substring(2)),
                )
                if (value) {
                    value.hours = item[binding]
                } else {
                    this.quarterlyValues.push({
                        job: item.job,
                        phase: item.phase,
                        yearQuarter: parseInt(binding.substring(2)),
                        hours: item[binding],
                    })
                }
            }
            //console.log('dtos', dtos)
            this.services.data.post('/api/ProjectedHours/SaveBulkHoursProjections', dtos).subscribe({
                error: () => {
                    this.services.notification.error('An error occured updating the projection.')
                },
            })
        } catch {
            this.services.notification.error('An error occured updating the projection. Refresh your browser to continue.')
        }
        this.updateWithQuarterlyData()
    }
}

export interface HoursProjection {
    job: string
    phase: string
    yearQuarter: number
    hours: number
}

export interface ProjectedHoursItem {
    jobDisplay: string
    job: string
    phaseDisplay: string
    phase: string
    departmentName: string
    remainingHours: number
    totalHoursProjected: number
    currentEstHrs: number
    actualHours: number
    progressPercentComplete: number | null | undefined
    variance: number
    startDate: Date
    endDate: DataMapEditor
}

export interface QuarterColumn {
    header: string
    binding: string
    width: number
    isReadOnly: boolean
}
